import React, { useState } from 'react';
import {
  Container,
  Grid,
  Box,
  Typography,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  Divider,
  Avatar,
  IconButton,
  Slide,
  Alert,
  Snackbar
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowBack as ArrowBackIcon,
  LocalShipping as ShippingIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Diamond as DiamondIcon
} from '@mui/icons-material';
import { useCart } from '../context/CartContext';
// import './CheckoutPage.css';

const steps = ['Shipping Address', 'Payment Method', 'Review Order'];

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, createOrder } = useCart();
  const [activeStep, setActiveStep] = useState(0);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Form states
  const [shippingDetails, setShippingDetails] = useState({
    fullName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: ''
  });

  const [paymentMethod, setPaymentMethod] = useState('razorpay');

  const handleShippingSubmit = () => {
    setActiveStep(1);
  };

  const handlePaymentSubmit = () => {
    setActiveStep(2);
  };

  const handlePlaceOrder = () => {
    // Create order
    const newOrder = createOrder({
      shippingDetails,
      paymentMethod,
      orderDate: new Date().toISOString()
    });

    setOrderId(newOrder.id);
    setOrderComplete(true);
    
    setSnackbar({
      open: true,
      message: '🎉 Order placed successfully!',
      severity: 'success'
    });

    setTimeout(() => {
      navigate('/orders');
    }, 3000);
  };

  const subtotal = getCartTotal();
  const shipping = subtotal > 50000 ? 0 : 500;
  const total = subtotal + shipping;

  if (orderComplete) {
    return (
      <Box className="checkout-success">
        <Container maxWidth="md" sx={{ py: 12 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, type: "spring" }}
            style={{ textAlign: 'center' }}
          >
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 10, -10, 0]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <CheckCircleIcon sx={{ fontSize: 100, color: '#4CAF50', mb: 3 }} />
            </motion.div>
            
            <Typography variant="h3" sx={{ fontFamily: 'Cormorant Garamond', color: '#C6A43F', mb: 2 }}>
              Thank You for Your Order!
            </Typography>
            
            <Typography variant="h5" sx={{ mb: 2 }}>
              Order #{orderId}
            </Typography>
            
            <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
              We've sent a confirmation to {shippingDetails.email}
            </Typography>

            <Paper sx={{ p: 4, mb: 4, bgcolor: alpha('#C6A43F', 0.05), borderRadius: '16px' }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#C6A43F' }}>
                What's Next?
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <ShippingIcon sx={{ fontSize: 40, color: '#C6A43F', mb: 1 }} />
                    <Typography variant="body2">Order Confirmed</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <DiamondIcon sx={{ fontSize: 40, color: '#C6A43F', mb: 1 }} />
                    <Typography variant="body2">Quality Check</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <ShippingIcon sx={{ fontSize: 40, color: '#C6A43F', mb: 1 }} />
                    <Typography variant="body2">On the Way</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/orders')}
                sx={{ borderColor: '#C6A43F', color: '#C6A43F' }}
              >
                Track Order
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate('/shop')}
                sx={{ bgcolor: '#C6A43F', color: '#0A0F1E' }}
              >
                Continue Shopping
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>
    );
  }

  return (
    <Box className="checkout-page">
      <Container maxWidth="lg" sx={{ py: 12 }}>
        {/* Header */}
        <motion.div
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: '#C6A43F' }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h3" sx={{ fontFamily: 'Cormorant Garamond', fontWeight: 700 }}>
              Checkout
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={4}>
          {/* Checkout Form */}
          <Grid item xs={12} md={8}>
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Paper sx={{ p: 4, borderRadius: '16px' }}>
                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                <AnimatePresence mode="wait">
                  {/* Step 1: Shipping Address */}
                  {activeStep === 0 && (
                    <motion.div
                      key="shipping"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <Typography variant="h6" sx={{ mb: 3, color: '#C6A43F' }}>
                        Shipping Address
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Full Name"
                            value={shippingDetails.fullName}
                            onChange={(e) => setShippingDetails({ ...shippingDetails, fullName: e.target.value })}
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Address Line 1"
                            value={shippingDetails.addressLine1}
                            onChange={(e) => setShippingDetails({ ...shippingDetails, addressLine1: e.target.value })}
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Address Line 2 (Optional)"
                            value={shippingDetails.addressLine2}
                            onChange={(e) => setShippingDetails({ ...shippingDetails, addressLine2: e.target.value })}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="City"
                            value={shippingDetails.city}
                            onChange={(e) => setShippingDetails({ ...shippingDetails, city: e.target.value })}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="State"
                            value={shippingDetails.state}
                            onChange={(e) => setShippingDetails({ ...shippingDetails, state: e.target.value })}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Pincode"
                            value={shippingDetails.pincode}
                            onChange={(e) => setShippingDetails({ ...shippingDetails, pincode: e.target.value })}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Phone Number"
                            value={shippingDetails.phone}
                            onChange={(e) => setShippingDetails({ ...shippingDetails, phone: e.target.value })}
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Email Address"
                            type="email"
                            value={shippingDetails.email}
                            onChange={(e) => setShippingDetails({ ...shippingDetails, email: e.target.value })}
                          />
                        </Grid>
                      </Grid>

                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                        <Button
                          variant="contained"
                          onClick={handleShippingSubmit}
                          disabled={!shippingDetails.fullName || !shippingDetails.addressLine1 || !shippingDetails.city || !shippingDetails.state || !shippingDetails.pincode || !shippingDetails.phone || !shippingDetails.email}
                          sx={{
                            bgcolor: '#C6A43F',
                            color: '#0A0F1E',
                            '&:hover': { bgcolor: '#B3923A' }
                          }}
                        >
                          Continue to Payment
                        </Button>
                      </Box>
                    </motion.div>
                  )}

                  {/* Step 2: Payment Method */}
                  {activeStep === 1 && (
                    <motion.div
                      key="payment"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <Typography variant="h6" sx={{ mb: 3, color: '#C6A43F' }}>
                        Payment Method
                      </Typography>

                      <FormControl component="fieldset">
                        <RadioGroup
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                        >
                          <Paper sx={{ p: 2, mb: 2, border: `1px solid ${paymentMethod === 'razorpay' ? '#C6A43F' : 'transparent'}` }}>
                            <FormControlLabel
                              value="razorpay"
                              control={<Radio sx={{ color: '#C6A43F' }} />}
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <img src="/razorpay-logo.png" alt="Razorpay" style={{ height: 30 }} />
                                  <Typography>Credit/Debit Card, UPI, NetBanking</Typography>
                                </Box>
                              }
                            />
                          </Paper>

                          <Paper sx={{ p: 2, mb: 2, border: `1px solid ${paymentMethod === 'cod' ? '#C6A43F' : 'transparent'}` }}>
                            <FormControlLabel
                              value="cod"
                              control={<Radio sx={{ color: '#C6A43F' }} />}
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <PaymentIcon sx={{ color: '#C6A43F' }} />
                                  <Typography>Cash on Delivery</Typography>
                                </Box>
                              }
                            />
                          </Paper>
                        </RadioGroup>
                      </FormControl>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                        <Button
                          variant="outlined"
                          onClick={() => setActiveStep(0)}
                          sx={{ borderColor: '#C6A43F', color: '#C6A43F' }}
                        >
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handlePaymentSubmit}
                          sx={{ bgcolor: '#C6A43F', color: '#0A0F1E' }}
                        >
                          Review Order
                        </Button>
                      </Box>
                    </motion.div>
                  )}

                  {/* Step 3: Review Order */}
                  {activeStep === 2 && (
                    <motion.div
                      key="review"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <Typography variant="h6" sx={{ mb: 3, color: '#C6A43F' }}>
                        Review Your Order
                      </Typography>

                      {/* Shipping Details Summary */}
                      <Paper sx={{ p: 3, mb: 3, bgcolor: alpha('#C6A43F', 0.05) }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                          Shipping Address
                        </Typography>
                        <Typography variant="body2">{shippingDetails.fullName}</Typography>
                        <Typography variant="body2">{shippingDetails.addressLine1}</Typography>
                        {shippingDetails.addressLine2 && (
                          <Typography variant="body2">{shippingDetails.addressLine2}</Typography>
                        )}
                        <Typography variant="body2">{shippingDetails.city}, {shippingDetails.state} - {shippingDetails.pincode}</Typography>
                        <Typography variant="body2">Phone: {shippingDetails.phone}</Typography>
                        <Typography variant="body2">Email: {shippingDetails.email}</Typography>
                      </Paper>

                      {/* Payment Method Summary */}
                      <Paper sx={{ p: 3, mb: 3, bgcolor: alpha('#C6A43F', 0.05) }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                          Payment Method
                        </Typography>
                        <Typography variant="body2">
                          {paymentMethod === 'razorpay' ? 'Online Payment (Razorpay)' : 'Cash on Delivery'}
                        </Typography>
                      </Paper>

                      {/* Order Items */}
                      <Paper sx={{ p: 3, mb: 3, bgcolor: alpha('#C6A43F', 0.05) }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                          Order Items
                        </Typography>
                        {cartItems.map((item) => (
                          <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">
                              {item.name} x {item.quantity}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              ₹{(item.finalPrice * item.quantity).toLocaleString()}
                            </Typography>
                          </Box>
                        ))}
                      </Paper>

                      {/* Order Total */}
                      <Paper sx={{ p: 3, bgcolor: '#0A0F1E', color: 'white' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography>Subtotal</Typography>
                          <Typography>₹{subtotal.toLocaleString()}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography>Shipping</Typography>
                          <Typography>{shipping === 0 ? 'Free' : `₹${shipping}`}</Typography>
                        </Box>
                        <Divider sx={{ my: 2, borderColor: 'rgba(198,164,63,0.3)' }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="h6">Total</Typography>
                          <Typography variant="h5" sx={{ color: '#C6A43F' }}>
                            ₹{total.toLocaleString()}
                          </Typography>
                        </Box>
                      </Paper>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                        <Button
                          variant="outlined"
                          onClick={() => setActiveStep(1)}
                          sx={{ borderColor: '#C6A43F', color: '#C6A43F' }}
                        >
                          Back
                        </Button>
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                          <Button
                            variant="contained"
                            size="large"
                            onClick={handlePlaceOrder}
                            sx={{
                              bgcolor: '#C6A43F',
                              color: '#0A0F1E',
                              px: 6,
                              '&:hover': { bgcolor: '#B3923A' }
                            }}
                          >
                            Place Order
                          </Button>
                        </motion.div>
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Paper>
            </motion.div>
          </Grid>

          {/* Order Summary */}
          <Grid item xs={12} md={4}>
            <Slide direction="left" in={true} timeout={800}>
              <Paper sx={{ p: 3, bgcolor: '#0A0F1E', color: 'white', borderRadius: '16px', position: 'sticky', top: '100px' }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#C6A43F' }}>
                  Order Summary
                </Typography>

                {cartItems.map((item) => (
                  <Box key={item.id} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Avatar
                      src={item.images?.[0]}
                      variant="rounded"
                      sx={{ width: 50, height: 50 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Qty: {item.quantity}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ₹{(item.finalPrice * item.quantity).toLocaleString()}
                    </Typography>
                  </Box>
                ))}

                <Divider sx={{ my: 3, borderColor: 'rgba(198,164,63,0.3)' }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography color="rgba(255,255,255,0.7)">Subtotal</Typography>
                  <Typography>₹{subtotal.toLocaleString()}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography color="rgba(255,255,255,0.7)">Shipping</Typography>
                  <Typography>{shipping === 0 ? 'Free' : `₹${shipping}`}</Typography>
                </Box>
                <Divider sx={{ my: 2, borderColor: 'rgba(198,164,63,0.3)' }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Total</Typography>
                  <Typography variant="h5" sx={{ color: '#C6A43F' }}>
                    ₹{total.toLocaleString()}
                  </Typography>
                </Box>
              </Paper>
            </Slide>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CheckoutPage;